import React, { useState } from 'react'
import { useToast } from '~/lib/ui/toast'
import { apiPatch, apiPost } from '~/util/api'
import { useMutation } from '@tanstack/react-query'
import { Textarea } from '~/lib/ui/textarea'
import { useCandidate, queryClient } from './CandidateProvider'
import { Label } from '~/lib/ui/label'
import { Button } from '~/lib/ui/button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectItem,
  SelectContent,
} from '~/lib/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { useCandidateCoachNotes } from './CoachNotesSection'
import { Checkbox } from '~/lib/ui/checkbox'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/lib/ui/accordion'
import PrivacySettings from './PrivacySettings'

const DurationSelect = ({ onValueChange, duration }) => {
  const durationOptions = [1, 2, 5, 10, 15, 30, 45, 60, 75, 90, 'Custom']
  const [hasCustomDuration, setHasCustomDuration] = useState(
    duration !== '' && !durationOptions.includes(duration),
  )
  const handleValueChange = (value) => {
    if (value === 'Custom') {
      setHasCustomDuration(true)
    } else {
      setHasCustomDuration(false)
      onValueChange(value)
    }
  }

  return (
    <div className="flex flex-row justify-evenly">
      <Select
        value={hasCustomDuration ? 'Custom' : duration.toString()}
        onValueChange={handleValueChange}
        className="w-full"
      >
        <SelectTrigger>
          <SelectValue placeholder="Select Duration" />
        </SelectTrigger>
        <SelectContent>
          {durationOptions.map((option) => (
            <SelectItem key={option} value={option.toString()}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hasCustomDuration && (
        <input
          type="number"
          className="ml-2 w-full"
          onChange={(e) => onValueChange(e.target.value)}
          placeholder="Custom Duration"
          value={duration}
        />
      )}
    </div>
  )
}

const CreateCoachNote = ({ coachNote, children }) => {
  const { candidateId } = useCandidate()

  const [open, setOpen] = useState(false)
  const [noteDetails, setNoteDetails] = useState(coachNote?.note || '')
  const [privacySettings, setPrivacySettings] = useState(
    coachNote?.db_privacy_settings || [],
  )
  const [isSaving, setIsSaving] = useState(false)
  const [selectedTopics, setSelectedTopics] = useState(
    coachNote?.db_topics || {},
  )
  const [duration, setDuration] = useState(coachNote?.duration || '')

  React.useEffect(() => {
    if (open) {
      setNoteDetails(coachNote?.note || '')
      setPrivacySettings(coachNote?.db_privacy_settings || [])
      setSelectedTopics(coachNote?.db_topics || {})
      setDuration(coachNote?.duration || '')
    }
  }, [open, coachNote])

  const coachNoteId = coachNote?.id || null
  const allPrivacySettings = ['csm', 'coach', 'admin']
  const formPrivacySettings =
    privacySettings?.indexOf('all') > -1
      ? allPrivacySettings
      : [...new Set(privacySettings)]
  const { toast } = useToast()

  const postCoachNote = async (formData) => {
    setIsSaving(true)
    const postPath = `/coach/candidates/${candidateId}/coach_notes.json`
    const response = await apiPost(postPath, formData, true)
    return response
  }

  const updateCoachNote = async (formData) => {
    setIsSaving(true)
    const updatePath = `/coach/candidates/${candidateId}/coach_notes/${coachNoteId}.json`
    const response = await apiPatch(updatePath, formData, true)
    return response
  }

  const mutationFn = coachNoteId ? updateCoachNote : postCoachNote
  const toastDescriptionText = coachNoteId ? 'updated' : 'created'
  const errorDescriptionText = coachNoteId ? 'updating' : 'creating'
  const errorDescription = coachNoteId
    ? 'while editing the coach note'
    : 'while creating the coach note'
  const toastTitle = coachNoteId ? 'Edit Coach Note' : 'Create Coach Note'

  const resetFormState = () => {
    setSelectedTopics({})
    setNoteDetails('')
    setDuration('')
    setPrivacySettings([])
  }

  const createCoachNoteMutation = useMutation({
    mutationFn: mutationFn,
    onSuccess: () => {
      setIsSaving(false)
      setOpen(false)
      queryClient.invalidateQueries(['serviceRequests', candidateId])
      resetFormState()
      toast({
        title: 'Success',
        description: `Coach Note ${toastDescriptionText} successfully.`,
      })
    },
    onError: (error) => {
      setIsSaving(false)
      console.error(`Error ${errorDescriptionText} resume revision:`, error)
      const description =
        error?.response?.data?.error ||
        error?.response?.data?.errors?.join(', ') ||
        `An error occurred ${errorDescription}.`
      console.log('Error description:', description)
      toast({
        description,
        title: `Could Not ${toastTitle}`,
        variant: 'destructive',
        duration: 10_000,
      })
    },
  })

  const saveClick = async () => {
    if (isSaving) return null
    const f = new FormData()
    f.append('coach_note[duration]', duration)
    f.append('coach_note[note]', noteDetails)
    f.append('coach_note[privacy_settings][]', 'self')
    formPrivacySettings.forEach((setting) => {
      f.append('coach_note[privacy_settings][]', setting)
    })
    Object.entries(selectedTopics).forEach(([category, topics]) => {
      topics.forEach((topic) => {
        f.append(`coach_note[topics][${category}][]`, topic)
      })
    })
    createCoachNoteMutation.mutate(f)
  }

  const dialogTitle = coachNoteId
    ? 'Edit the details of this Coach Note'
    : 'Create a new Coach Note'

  const showTopics = !(coachNote?.resume_revision || coachNote?.service_request)
  return (
    <Dialog
      open={open}
      onOpenChange={() => setOpen(!open)}
      className="rounded-2xl"
    >
      <DialogTrigger asChild className="cursor-pointer">
        <Button
          variant="link"
          className="m-0 justify-start p-0 font-normal text-intoo-blue-medium hover:text-link"
        >
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="font-roboto lg:max-w-3xl xl:max-w-5xl"
        variant="default"
        dialogCloseClass="text-black"
      >
        <DialogHeader>
          <DialogTitle className="text-left" asChild>
            <h2 className="text-2xl">{dialogTitle}</h2>
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-auto px-4 pb-4">
          <div className="pb-4">
            <Label htmlFor="duration" className="text-lg !font-medium">
              Duration (Minutes)
            </Label>
            <DurationSelect onValueChange={setDuration} duration={duration} />
          </div>
          {showTopics && (
            <div className="pb-4">
              <Label htmlFor="duration" className="text-lg !font-medium">
                Topics
              </Label>
              <Topics
                selectedTopics={selectedTopics}
                setSelectedTopics={setSelectedTopics}
              />
            </div>
          )}
          <div className="pb-4">
            <Label htmlFor="noteDetails" className="text-lg !font-medium">
              Note Details
            </Label>
            <Textarea
              className="w-full"
              name="noteDetails"
              value={noteDetails}
              onChange={(e) => setNoteDetails(e.target.value)}
            />
          </div>
          <div className="pb-4">
            <PrivacySettings
              setPrivacySettings={setPrivacySettings}
              privacySettings={privacySettings}
            />
          </div>
          <div className="flex flex-row justify-end">
            <Button className="inline" onClick={saveClick} disabled={isSaving}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
export default CreateCoachNote

const Topics = ({ selectedTopics, setSelectedTopics }) => {
  const { candidateCoachNotes } = useCandidateCoachNotes()
  const { coachNoteTopics } = candidateCoachNotes || {}

  return (
    <div className="grid grid-cols-2 gap-2">
      {coachNoteTopics?.map((topicGroup, idx) => (
        <TopicGroup
          key={topicGroup.category}
          topicGroup={topicGroup}
          setSelectedTopics={setSelectedTopics}
          selectedTopics={selectedTopics}
          idx={idx}
        />
      ))}
    </div>
  )
}

const TopicGroup = ({ topicGroup, selectedTopics, setSelectedTopics, idx }) => {
  const { category, topics } = topicGroup

  const expanded =
    selectedTopics[category] && selectedTopics[category].length > 0

  const expandedProps = expanded ? { defaultValue: `item-${idx}` } : {}

  return (
    <div>
      <Accordion type="single" collapsible {...expandedProps}>
        <AccordionItem value={`item-${idx}`}>
          <AccordionTrigger>
            <h3>{category}</h3>
          </AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-2">
              {topics.map((topic, idx) => {
                return (
                  <TopicCheckbox
                    key={`${topic}-${idx}`}
                    selectedTopics={selectedTopics}
                    setSelectedTopics={setSelectedTopics}
                    topic={topic}
                    category={category}
                  />
                )
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}

const TopicCheckbox = ({
  topic,
  category,
  selectedTopics,
  setSelectedTopics,
}) => {
  const checked = selectedTopics[category]?.includes(topic.topic)

  const handleCheckboxChange = () => {
    const newTopics = { ...selectedTopics }
    if (!newTopics[category]) {
      newTopics[category] = []
    }
    if (checked) {
      newTopics[category] = newTopics[category].filter((t) => t !== topic.topic)
    } else {
      newTopics[category].push(topic.topic)
    }
    setSelectedTopics(newTopics)
  }

  return (
    <div className="flex items-center gap-2">
      <Checkbox
        onCheckedChange={handleCheckboxChange}
        checked={checked}
        id={topic.topic}
      />
      <Label htmlFor={topic.topic}>{topic?.topic}</Label>
    </div>
  )
}
